import React, { memo, useMemo } from "react";
import QuestionContent from "./QuestionContent";

const VirtualizedQuestionList = memo(
  ({
    elements,
    plotLanguage,
    klausimas,
    visiAts,
    atsakymai,
    tipai,
    metai,
    handleCapture,
    questionRef,
    activeQuestionId,
    setActiveQuestionId,
    expandedQuestions,
    onQuestionExpand,
    windowSize = 10,
  }) => {
    // Track visible range for windowing
    const [visibleRange, setVisibleRange] = React.useState({
      start: 0,
      end: windowSize,
    });
    // Memoize the filtered and mapped items
    const renderedItems = useMemo(() => {
      // Early return if no elements
      if (!elements || elements.length === 0) {
        return [];
      }

      // Map elements to QuestionContent components
      return elements
        .map((element) => {
          if (!element || !element.name) return null;

          return (
            <QuestionContent
              key={element.name}
              element={element}
              plotLanguage={plotLanguage}
              klausimas={klausimas}
              visiAts={visiAts}
              atsakymai={atsakymai}
              tipai={tipai}
              metai={metai}
              handleCapture={handleCapture}
              questionRef={questionRef}
              isActive={activeQuestionId === element.name}
              setActiveQuestionId={setActiveQuestionId}
              isExpanded={expandedQuestions.has(element.name)}
              onExpand={onQuestionExpand}
            />
          );
        })
        .filter(Boolean); // Remove any null items
    }, [
      elements,
      plotLanguage,
      klausimas,
      visiAts,
      atsakymai,
      tipai,
      metai,
      handleCapture,
      questionRef,
      activeQuestionId,
      setActiveQuestionId,
      expandedQuestions,
      onQuestionExpand,
    ]);

    // Intersection Observer for infinite scroll
    const observerTarget = React.useRef(null);

    React.useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            visibleRange.end < renderedItems.length
          ) {
            setVisibleRange((prev) => ({
              start: prev.start,
              end: Math.min(prev.end + windowSize, renderedItems.length),
            }));
          }
        },
        { threshold: 0.5 }
      );

      if (observerTarget.current) {
        observer.observe(observerTarget.current);
      }

      return () => {
        if (observerTarget.current) {
          observer.unobserve(observerTarget.current);
        }
      };
    }, [renderedItems.length, visibleRange.end, windowSize]);

    // Return early if no items to render
    if (!renderedItems.length) {
      return null;
    }

    return (
      <div
        className="questions-container"
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "12px",
        }}
      >
        {renderedItems.slice(visibleRange.start, visibleRange.end)}
        <div ref={observerTarget} style={{ height: "20px" }} />
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Always re-render when answers change to ensure diagrams update with filtering
    if (prevProps.atsakymai !== nextProps.atsakymai) {
      return false;
    }

    // Always re-render when expanded questions change
    const prevExpanded = prevProps.expandedQuestions;
    const nextExpanded = nextProps.expandedQuestions;
    if (prevExpanded !== nextExpanded) {
      return false;
    }

    // Deep comparison for other props
    const areElementsSame = prevProps.elements === nextProps.elements;
    const isPlotLanguageSame =
      prevProps.plotLanguage === nextProps.plotLanguage;
    const isKlausimasSame = prevProps.klausimas === nextProps.klausimas;
    const isVisiAtsSame = prevProps.visiAts === nextProps.visiAts;
    const isTipaiSame = prevProps.tipai === nextProps.tipai;
    const isMetaiSame = prevProps.metai === nextProps.metai;
    const isActiveQuestionIdSame =
      prevProps.activeQuestionId === nextProps.activeQuestionId;
    const isWindowSizeSame = prevProps.windowSize === nextProps.windowSize;

    return (
      areElementsSame &&
      isPlotLanguageSame &&
      isKlausimasSame &&
      isVisiAtsSame &&
      isTipaiSame &&
      isMetaiSame &&
      isActiveQuestionIdSame &&
      isWindowSizeSame
    );
  }
);

VirtualizedQuestionList.displayName = "VirtualizedQuestionList";

export default VirtualizedQuestionList;
